import { useState } from "react";
import { useTranslation } from "react-i18next";

import useUser from "hooks/useUser";
import useQuery from "hooks/useQuery";
import useMutation from "hooks/useMutation";
import Consumer from "App/EHR/V2/Prescription/Consumer";
import getConsumerIdentification from "App/EHR/V2/getConsumerIdentification";

// [Fase 3, MEET_AI_SUMMARY_PLAN.md] Antes "Nueva videollamada" abría una sala
// random sin turno (openRoom() sin id) — sin turno, meet no tiene con qué
// asociar el resumen de IA a un paciente (ai_summary.runs.event_id es NOT
// NULL). Este modal ofrece elegir un paciente y crear el turno AL VUELO
// (mismo POST que Events/Form/Form.js) para que la sala nazca ya asociada, o
// mantener el comportamiento de siempre si no hace falta resumen (pedido
// explícito de Nacho: preservar la opción de una videollamada espontánea sin
// paciente).
//
// Modal propio (no Bootstrap JS) a propósito — mismo patrón que
// Components/ConfirmModal/ConfirmModal.js: useStoreRooms.open() (ver su
// comentario) ya se encarga de cerrar cualquier modal Bootstrap abierto al
// abrir la sala, pero no sabe nada de ESTE modal (no es una instancia de
// bootstrap.Modal) — se cierra solo por su propio estado `show`.
const NewRemoteCall = ({ show, onClose, openRoom }) => {
  const { t } = useTranslation();
  const user = useUser();
  const [consumer, setConsumer] = useState({});

  const resource = useQuery(window._l(`/${user.team?.resourceID}`).substr(1), {
    enabled: !!user.team?.resourceID && show,
  });

  const [mutate, mutation] = useMutation(window._l("/events").substr(1), {
    onSuccess: (data) => {
      openRoom(`${data.id}/${data.token}`);
      close();
    },
    onError: (error) => {
      window.notify({ message: error.message, type: "danger", title: "Error" });
    },
  });

  const close = () => {
    setConsumer({});
    if (onClose) onClose();
  };

  if (!show) return null;

  const now = new Date();
  const startWithAiSummary = () => {
    if (!consumer.id || !user.team?.resourceID) return;
    mutate({
      consumer: { id: consumer.id, label: consumer.label },
      resource: { id: user.team.resourceID, label: resource.data?.label },
      day: now.toLocaleDateString("fr-ca"),
      time: now.toLocaleTimeString("it-IT", { timeStyle: "short" }),
      remote: true,
      status: "booked",
      type: "appointment",
      notify: true,
      compute: true,
    });
  };

  const continueWithoutPatient = () => {
    openRoom();
    close();
  };

  return (
    <div
      className="modal show d-flex align-items-center justify-content-center"
      tabIndex="-1"
      role="dialog"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", minHeight: "100vh" }}
    >
      <div className="modal-dialog" role="document">
        <div className="modal-content">
          <div className="modal-body p-4">
            <div className="d-flex justify-content-between">
              <h5>{t("new", { label: t("remote_link"), context: "female" })}</h5>
              <button
                type="button"
                className="btn-close"
                aria-label={t("close", "Cerrar")}
                data-testid="new-remote-call-close"
                onClick={close}
              />
            </div>

            <div className="mt-3 mb-2">
              <label className="form-label small mb-1">
                {t("navbar_new_remote_call_consumer_label", "Paciente (opcional, para generar un resumen de IA de la consulta)")}
              </label>
              <Consumer.Selector
                onChange={(value) => setConsumer(value || {})}
              />
              {/* Bug real (seguimiento 2026-09-10, Juan Cruz QA): Consumer.Selector
                  usa Typeahead con selected={[]} SIEMPRE (ver Consumer.js) — el
                  input vuelve a quedar vacío apenas se elige un paciente, sin
                  dejar ningún rastro de la selección. El profesional no tenía
                  forma de confirmar que su elección quedó registrada. Este chip
                  muestra nombre/apellido + documento del paciente elegido, con
                  una "x" para deshacer la selección sin tener que buscar de
                  nuevo. */}
              {consumer.id && (
                <div
                  className="d-flex align-items-center justify-content-between bg-light border rounded px-2 py-1 mt-2"
                  data-testid="new-remote-call-selected-consumer"
                >
                  <div className="text-truncate">
                    <span className="fw-bold">{consumer.label}</span>
                    {consumer.identification && (
                      <span className="text-secondary ms-2 small">
                        {t(consumer.identificationType || "dni", "DNI")} {getConsumerIdentification(consumer.identification)}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn-close ms-2"
                    aria-label={t("remove", "Quitar")}
                    data-testid="new-remote-call-clear-consumer"
                    onClick={() => setConsumer({})}
                  />
                </div>
              )}
            </div>

            <div className="d-flex flex-column gap-2 mt-3">
              <button
                type="button"
                className="btn btn-primary"
                data-testid="new-remote-call-start"
                disabled={!consumer.id || mutation.isLoading}
                onClick={startWithAiSummary}
              >
                {mutation.isLoading
                  ? t("saving", "Guardando...")
                  : t("navbar_new_remote_call_start", "Iniciar Videoconsulta")}
              </button>
              <button
                type="button"
                className="btn btn-link"
                data-testid="new-remote-call-continue-without-patient"
                disabled={mutation.isLoading}
                onClick={continueWithoutPatient}
              >
                {t("navbar_new_remote_call_continue_without_patient", "Continuar sin paciente")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewRemoteCall;
